import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, Save, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { contentService, adminService } from '../../services/api';

const AdminFAQ = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const { toast } = useToast();
  
  const categories = [
    { id: 'general', label: '常見問題 / General' },
    { id: 'labor', label: '勞工法例問題 / Labor Law' }
  ];

  const [faqs, setFaqs] = useState([
    { id: '1', category: 'general', question: '聘請外傭需要多少時間？', questionEn: '', answer: '一般來說，聘請海外女傭需時約8-12星期，包括簽署合約、領事館公證、入境處批核及安排到港等程序。聘請完約現成女傭則較快，約4-6星期可完成。', answerEn: '' },
  ]);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await contentService.get();
      if (response.data?.faqs?.length > 0) {
        // Ensure all FAQs have English fields
        const faqsWithEn = response.data.faqs.map(faq => ({
          ...faq,
          questionEn: faq.questionEn || '',
          answerEn: faq.answerEn || ''
        }));
        setFaqs(faqsWithEn);
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Get current content and update faqs
      const currentContent = await contentService.get();
      const updatedContent = {
        ...currentContent.data,
        faqs: faqs
      };
      
      await adminService.content.update(updatedContent);
      
      toast({
        title: '成功 / Success',
        description: '常見問題已保存 / FAQs saved'
      });
    } catch (error) {
      console.error('Error saving FAQs:', error);
      toast({
        title: '錯誤 / Error',
        description: '保存失敗，請再試一次 / Save failed, please try again',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const addFAQ = () => {
    const newFaq = {
      id: Date.now().toString(),
      category: 'general',
      question: '',
      questionEn: '',
      answer: '',
      answerEn: ''
    };
    setFaqs([...faqs, newFaq]);
    setExpandedIndex(faqs.length);
  };

  const removeFAQ = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
    setExpandedIndex(null);
  };

  const updateFAQ = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-12 w-12 animate-spin" style={{ color: '#00b6a5' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#00b6a5' }}>常見問題管理 / FAQ Management</h1>
          <p className="text-gray-600 mt-2">編輯網站常見問題內容（中英文）/ Edit FAQ content (Chinese & English)</p>
        </div>
        <Button onClick={addFAQ} style={{ backgroundColor: '#00b6a5' }}>
          <Plus className="h-4 w-4 mr-2" />
          新增問題 / Add FAQ
        </Button>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <Card key={faq.id} className="overflow-hidden">
            <div
              className="p-4 cursor-pointer flex justify-between items-center"
              style={{ backgroundColor: expandedIndex === index ? '#f8fffe' : 'white' }}
              onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
            >
              <div className="flex-1">
                <span className="text-xs px-2 py-1 rounded mr-2" style={{ backgroundColor: '#00b6a5', color: 'white' }}>
                  {categories.find(c => c.id === faq.category)?.label || 'General'}
                </span>
                <span className="font-medium">
                  {faq.question || '(未填寫問題 / No question)'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFAQ(index);
                  }}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
                {expandedIndex === index ? (
                  <ChevronUp className="h-5 w-5 text-gray-500" />
                ) : (
                  <ChevronDown className="h-5 w-5 text-gray-500" />
                )}
              </div>
            </div>
            
            {expandedIndex === index && (
              <CardContent className="pt-4 space-y-4 border-t">
                <div>
                  <label className="block text-sm font-medium mb-2">分類 / Category</label>
                  <select
                    value={faq.category}
                    onChange={(e) => updateFAQ(index, 'category', e.target.value)}
                    className="w-full p-2 border rounded-md"
                  >
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Chinese Content */}
                <div className="border-b pb-4">
                  <h4 className="font-semibold text-gray-700 mb-3">中文內容 Chinese Content</h4>
                  <div>
                    <label className="block text-sm font-medium mb-2">問題（中文）* Question (Chinese)</label>
                    <Input
                      value={faq.question}
                      onChange={(e) => updateFAQ(index, 'question', e.target.value)}
                      placeholder="輸入中文問題..."
                    />
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2">答案（中文）* Answer (Chinese)</label>
                    <Textarea
                      value={faq.answer}
                      onChange={(e) => updateFAQ(index, 'answer', e.target.value)}
                      placeholder="輸入中文答案..."
                      rows={4}
                    />
                  </div>
                </div>

                {/* English Content */}
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-blue-800 mb-3">📝 English Content 英文內容</h4>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-blue-700">Question (English)</label>
                    <Input
                      value={faq.questionEn || ''}
                      onChange={(e) => updateFAQ(index, 'questionEn', e.target.value)}
                      placeholder="Enter English question..."
                      className="border-blue-300"
                    />
                  </div>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-2 text-blue-700">Answer (English)</label>
                    <Textarea
                      value={faq.answerEn || ''}
                      onChange={(e) => updateFAQ(index, 'answerEn', e.target.value)}
                      placeholder="Enter English answer..."
                      rows={4}
                      className="border-blue-300"
                    />
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        ))}

        {faqs.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p>尚未新增任何問題 / No FAQs added yet</p>
            <Button onClick={addFAQ} className="mt-4" variant="outline">
              <Plus className="h-4 w-4 mr-2" />
              新增第一個問題 / Add First FAQ
            </Button>
          </div>
        )}
      </div>

      {/* Save Button */}
      <div className="flex justify-end mt-8 pb-24">
        <Button
          onClick={handleSave}
          disabled={saving}
          size="lg"
          style={{ backgroundColor: '#00b6a5' }}
          className="text-white"
        >
          {saving ? (
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
          ) : (
            <Save className="h-5 w-5 mr-2" />
          )}
          保存所有更改 / Save All Changes
        </Button>
      </div>
    </div>
  );
};

export default AdminFAQ;
